import { inject, Injectable } from '@angular/core';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { PublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { EmployerAccessService } from './employer-access.service';
import { TelemetryService } from '../../../shared/services/telemetry.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private msalInstance = inject(MSAL_INSTANCE) as unknown as PublicClientApplication;
  private employerAccessService = inject(EmployerAccessService);
  private telemetryService = inject(TelemetryService);
  private router = inject(Router);
  constructor() {}

  get account() {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      return accounts[0];
    }
    return null;
  }

  get fullName(): string | null {
    const acc = this.account;
    if (acc?.idTokenClaims && typeof acc.idTokenClaims === 'object') {
      const claims = acc.idTokenClaims as { given_name?: unknown };
      return typeof claims.given_name === 'string' ? claims.given_name : null;
    }
    return null;
  }

  async login() {
    this.employerAccessService.clear();
    this.clearStaleInteractionLocks();
    const request: RedirectRequest = {
      scopes: ['openid', 'profile', 'phone', 'offline_access', ...environment.azureB2C.msalScopes],
    };

    try {
      await this.msalInstance.loginRedirect(request);
    } catch (error: any) {
      const code = error?.errorCode || error?.code || '';
      if (String(code).includes('interaction_in_progress')) {
        this.clearStaleInteractionLocks();
        await new Promise((resolve) => setTimeout(resolve, 300));
        await this.msalInstance.loginRedirect(request);
        return;
      }

      this.telemetryService.trackException(error, {
        source: 'AuthService',
        step: 'loginRedirect',
      });
      throw error;
    }
  }

  async logout(): Promise<void> {
    const account = this.msalInstance.getActiveAccount() ?? this.msalInstance.getAllAccounts()[0];

    this.employerAccessService.clear();
    this.msalInstance.setActiveAccount(null);
    sessionStorage.removeItem('activePolicy');
    localStorage.removeItem('loginAttempted');
    localStorage.removeItem('authError');
    this.clearStaleInteractionLocks();

    if (!account) {
      await this.msalInstance.clearCache().catch(() => undefined);
      await this.router.navigateByUrl('/auth');
      return;
    }

    try {
      await this.msalInstance.clearCache();
      await this.msalInstance.logoutRedirect({
        account,
        authority: this.buildAuthorityFromAccount(account),
        postLogoutRedirectUri: `${window.location.origin}/auth`,
      });
    } catch (error) {
      this.telemetryService.trackException(error, {
        source: 'AuthService',
        step: 'logoutRedirect',
      });
      await this.router.navigateByUrl('/auth');
    }
  }

  private buildAuthorityFromAccount(account: any): string {
    const claims = account?.idTokenClaims as { tfp?: unknown; acr?: unknown } | undefined;
    const rawPolicy = claims?.tfp ?? claims?.acr;
    const policy = typeof rawPolicy === 'string' ? rawPolicy : '';

    const base = environment.azureB2C.knownAuthorities[0];
    const tenant = environment.azureB2C.domain;

    if (!policy) {
      return environment.azureB2C.authority;
    }

    return `https://${base}/${tenant}/${policy.toLowerCase()}`;
  }

  private clearStaleInteractionLocks() {
    if (typeof window === 'undefined') {
      return;
    }

    const stores = [window.localStorage, window.sessionStorage];
    for (const store of stores) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (!key) {
          continue;
        }
        if (key.includes('interaction.status')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => store.removeItem(key));
    }
  }


  get loggedIn(): boolean {
    return this.msalInstance.getAllAccounts().length > 0;
  }
}
